import type { DrawObject, Tool } from "../types/document.ts";
import type { Action, AppState } from "../state/actions.ts";
import { DrawingCanvas } from "./drawing-canvas.tsx";
import { TextOverlay } from "./text-overlay.tsx";

interface Props {
  objects: DrawObject[];
  inProgress: DrawObject | null;
  canvasWidth: number;
  canvasHeight: number;
  activeTool: Tool;
  activeEmoji: string | undefined;
  activeEmojiSize: number;
  textHtml: string;
  textStyle: AppState["textStyle"];
  dispatch: (action: Action) => void;
}

const headerStyle = {
  background: "#F0E2C0",
  borderBottom: "1px solid #C4A97D",
  padding: "8px 16px",
  display: "flex",
  alignItems: "stretch",
  gap: "12px",
  fontFamily: "Georgia, serif",
  color: "#5D4037",
} as const;

const infoBoxStyle = {
  border: "1.5px solid #B8956A",
  padding: "4px 8px",
  fontSize: "7px",
  lineHeight: "1.4",
  color: "#7D6B5D",
  fontFamily: "Georgia, serif",
  flex: "0 0 auto",
  width: "120px",
  background: "#F5E6C8",
} as const;

const infoBoxTitleStyle = {
  fontWeight: 700,
  fontSize: "8px",
  fontVariant: "small-caps",
  textAlign: "center" as const,
  marginBottom: "3px",
  paddingBottom: "3px",
  borderBottom: "1px solid #B8956A",
  marginLeft: "4px",
  marginRight: "4px",
} as const;

const symbolsBoxStyle = {
  ...infoBoxStyle,
} as const;

const footerStyle = {
  background: "#F0E2C0",
  borderTop: "1px solid #C4A97D",
  padding: "4px 16px",
  fontSize: "9px",
  color: "#7D6B5D",
  fontFamily: "Georgia, serif",
  fontWeight: 600,
  fontVariant: "small-caps",
  textAlign: "center" as const,
} as const;

export function Workspace(props: Props) {
  return (
    <div
      style={{
        width: props.canvasWidth,
        border: "2px solid #B8956A",
        borderRadius: "4px",
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(139,115,85,0.2)",
        background: "#F0E2C0",
      }}
    >
      {/* telegram header */}
      <div style={headerStyle}>
        <div style={infoBoxStyle}>
          <div style={infoBoxTitleStyle}>Class of Service</div>
          <div>
            This is a full-rate Telegram or Cable&shy;gram unless its de&shy;ferred character is
            indicated by a suitable symbol above or preceding the address.
          </div>
        </div>
        <div
          style={{
            flex: 1,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontSize: "28px",
              fontWeight: 700,
              letterSpacing: "6px",
              fontFamily: "'Georgia', serif",
              color: "#5D4037",
            }}
          >
            WESTERN UNION
          </div>
          <div
            style={{
              fontSize: "8px",
              color: "#7D6B5D",
              marginTop: "2px",
              fontVariant: "small-caps",
              fontFamily: "Georgia, serif",
            }}
          >
            Theodore E. Bear, President
          </div>
        </div>
        <div style={symbolsBoxStyle}>
          <div style={infoBoxTitleStyle}>Symbols</div>
          <div>DL = Day Letter</div>
          <div>NL = Night Letter</div>
          <div>LT = Int'l Letter Telegram</div>
          <div>VLT = Int'l Victory Ltr.</div>
        </div>
      </div>

      {/* sub-header line */}
      <div
        style={{
          background: "#F0E2C0",
          borderBottom: "1px solid #C4A97D",
          padding: "3px 16px",
          fontSize: "7px",
          color: "#7D6B5D",
          fontFamily: "Georgia, serif",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>
          The filing time shown in the date line on telegrams and day letters is{" "}
          <span style={{ textTransform: "uppercase", fontWeight: 600 }}>Standard Time</span> at
          point of origin.
        </span>
        <span>
          Time of receipt is{" "}
          <span style={{ textTransform: "uppercase", fontWeight: 600 }}>Standard Time</span> at
          point of destination
        </span>
      </div>

      {/* canvas area */}
      <div class="relative" style={{ width: props.canvasWidth, height: props.canvasHeight }}>
        <DrawingCanvas
          objects={props.objects}
          inProgress={props.inProgress}
          canvasWidth={props.canvasWidth}
          canvasHeight={props.canvasHeight}
          activeTool={props.activeTool}
          activeEmoji={props.activeEmoji}
          activeEmojiSize={props.activeEmojiSize}
          dispatch={props.dispatch}
        />
        <TextOverlay
          html={props.textHtml}
          style={props.textStyle}
          isActive={props.activeTool === "text"}
          dispatch={props.dispatch}
        />
      </div>

      {/* footer */}
      <div style={footerStyle}>
        The Company Will Appreciate Suggestions From Its Patrons Concerning Its Service
      </div>
    </div>
  );
}
